import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

import { QuestionListFilter, TQuestion } from "../../api/type"
import { LoaderComponent } from "../../utils/Helper"
import QuestionActionDropdown from "./components/QuestionActionDropdown"
import QuestionConfirmModals from "./components/QuestionConfirmModals"
import QuestionListItem from "./components/QuestionListItem"
import { useQuestionActions } from "./hooks/useQuestionActions"
import { useQuestionSubjects } from "./hooks/useQuestionSubjects"

type IQuestionListProps = {
  totalPage: number
  data: TQuestion[]
  loading: boolean
  isLoadingMore: boolean
  filter: QuestionListFilter
  setFilter: React.Dispatch<React.SetStateAction<QuestionListFilter>>
  getQuestion: (
    nextFilter?: Partial<QuestionListFilter>,
    isPagination?: boolean
  ) => void
  onRemoveQuestion: (id: string) => void
  onUpdateQuestion: (id: string, patch: Partial<TQuestion>) => void
}

const QuestionList = ({
  data,
  loading,
  isLoadingMore,
  totalPage,
  filter,
  setFilter,
  getQuestion,
  onRemoveQuestion,
  onUpdateQuestion,
}: IQuestionListProps) => {
  const navigate = useNavigate()
  const { subjectById } = useQuestionSubjects()
  const paginationDivRef = useRef<HTMLDivElement>(null)
  const [actionMenu, setActionMenu] = useState<{
    index: number
    top: number
    left: number
  } | null>(null)
  const [openedQuestion, setOpenedQuestion] = useState<number | null>(null)

  const {
    confirmType,
    pendingStatus,
    pendingLearnerValue,
    pendingVerification,
    isConfirmLoading,
    closeConfirm,
    handleConfirm,
    handleEdit,
    openLearnerPublishConfirm,
    openDeleteConfirm,
    openPublishConfirm,
    openArchiveConfirm,
    openRestoreConfirm,
    openVerifyConfirm,
  } = useQuestionActions({
    onRemove: onRemoveQuestion,
    onUpdate: onUpdateQuestion,
  })

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          totalPage > filter.page_number &&
          !loading &&
          !isLoadingMore
        ) {
          const nextPage = {
            ...filter,
            page_number: filter.page_number + 1,
          }

          setFilter((prev: QuestionListFilter) => ({
            ...prev,
            ...nextPage,
          }))

          getQuestion(nextPage, true)
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.5,
      }
    )

    if (paginationDivRef.current) observer.observe(paginationDivRef.current)

    return () => {
      if (paginationDivRef.current) observer.unobserve(paginationDivRef.current)
    }
  }, [filter, totalPage, loading, isLoadingMore, getQuestion, setFilter])

  useEffect(() => {
    if (actionMenu === null) return

    const closeMenu = () => setActionMenu(null)
    const listElement = paginationDivRef.current?.closest(".question_list")

    window.addEventListener("resize", closeMenu)
    listElement?.addEventListener("scroll", closeMenu)

    return () => {
      window.removeEventListener("resize", closeMenu)
      listElement?.removeEventListener("scroll", closeMenu)
    }
  }, [actionMenu])

  const closeActionMenu = () => setActionMenu(null)

  const toggleActionMenu = (
    index: number,
    event: React.MouseEvent<SVGElement>
  ) => {
    event.stopPropagation()

    if (actionMenu?.index === index) {
      closeActionMenu()
      return
    }

    const rect = event.currentTarget.getBoundingClientRect()
    const menuWidth = 180

    setActionMenu({
      index,
      top: rect.bottom + 6,
      left: Math.max(8, rect.right - menuWidth),
    })
  }

  const viewQuestion = (question: TQuestion) => {
    if (!question.id) return
    navigate(`/questions/view-question/${question.id}`)
  }

  const toggleExpandedQuestion = (index: number) => {
    setOpenedQuestion((current) => (current === index ? null : index))
  }

  const activeQuestion =
    actionMenu !== null ? data[actionMenu.index] : undefined

  return (
    <div className="question_list">
      {!loading && !isLoadingMore && data.length === 0 && (
        <p className="question_list__empty">Question Not found!</p>
      )}

      {loading && data.length === 0 && (
        <div className="question_list__initial-loading">
          <LoaderComponent label="Loading questions…" />
        </div>
      )}

      {actionMenu !== null && (
        <div
          className="action_dropdown-backdrop"
          onClick={closeActionMenu}
          aria-hidden="true"
        />
      )}

      {data.map((item, index) => (
        <QuestionListItem
          key={item.id}
          item={item}
          index={index}
          isOpen={openedQuestion === index}
          isActionOpen={actionMenu?.index === index}
          levelLabels={subjectById(item.subject)?.level_labels}
          onToggleExpand={() => toggleExpandedQuestion(index)}
          onToggleActionMenu={(event) => toggleActionMenu(index, event)}
        />
      ))}

      {actionMenu !== null && activeQuestion && (
        <QuestionActionDropdown
          question={activeQuestion}
          style={{ top: actionMenu.top, left: actionMenu.left }}
          onView={() => {
            closeActionMenu()
            viewQuestion(activeQuestion)
          }}
          onEdit={() => {
            closeActionMenu()
            handleEdit(activeQuestion)
          }}
          onPublish={() => {
            closeActionMenu()
            openPublishConfirm(activeQuestion)
          }}
          onArchive={() => {
            closeActionMenu()
            openArchiveConfirm(activeQuestion)
          }}
          onActivate={() => {
            closeActionMenu()
            openRestoreConfirm(activeQuestion, "published")
          }}
          onMoveToDraft={() => {
            closeActionMenu()
            openRestoreConfirm(activeQuestion, "draft")
          }}
          onToggleLearnerPublish={() => {
            closeActionMenu()
            openLearnerPublishConfirm(
              activeQuestion,
              !activeQuestion.published_to_learner
            )
          }}
          onVerify={() => {
            closeActionMenu()
            openVerifyConfirm(activeQuestion, "verified")
          }}
          onReject={() => {
            closeActionMenu()
            openVerifyConfirm(activeQuestion, "rejected")
          }}
          onDelete={() => {
            closeActionMenu()
            if (activeQuestion.id) openDeleteConfirm(activeQuestion.id)
          }}
        />
      )}

      <div className="question_list__pagination-sentinel" ref={paginationDivRef}>
        {isLoadingMore && (
          <p className="question_list__loading-more" aria-live="polite">
            Loading more questions…
          </p>
        )}
      </div>

      <QuestionConfirmModals
        confirmType={confirmType}
        pendingStatus={pendingStatus}
        pendingLearnerValue={pendingLearnerValue}
        pendingVerification={pendingVerification}
        isLoading={isConfirmLoading}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />
    </div>
  )
}

export default QuestionList
